import { useEffect, useState } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { query as sq } from "@/lib/supabase-utils";
import { Sentry } from "@/lib/sentry";
import { User, Session } from "@supabase/supabase-js";
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarProvider, SidebarTrigger, useSidebar } from "@/components/ui/sidebar";
import { NavLink } from "@/components/NavLink";
import { Package, TrendingUp, ShoppingCart, Users, LogOut, FileText, User as UserIcon, FileSearch, Truck, Settings, LayoutDashboard, Shield, Settings2, Receipt, Activity, History } from "lucide-react";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { useModulePermissions } from "@/hooks/useModulePermissions";
import { useAmazonModule } from "@/hooks/useAmazonModule";
import { useCronAlertCount } from "@/hooks/useSupabaseQueries";
import { prefetchRoute } from "@/lib/page-imports";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import nannukLogo from "@/assets/nannuk-logo-full.png";
import nannukIcon from "@/assets/nannuk-icon.png";

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const MODULE_KEY_MAP: Record<string, string> = {
  "/dashboard/overview": "overview",
  "/dashboard": "inventory",
  "/dashboard/movements": "movements",
  "/dashboard/orders": "orders",
  "/dashboard/invoices": "invoices",
  "/dashboard/facturapro": "facturapro",
  "/dashboard/labeling": "labeling",
  "/dashboard/check-invoices": "check-invoices",
  "/dashboard/users": "users",
  "/dashboard/clients": "users",
  "/dashboard/product-mappings": "product-mappings",
  "/dashboard/settings": "settings",
  "/dashboard/administration": "administration",
};
const DashboardSidebar = ({ userRole, userEmail, userId }: { userRole: string; userEmail: string | undefined; userId: string | undefined }) => {
  const { state } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();

  const { hasAccess } = useModulePermissions(userId, userRole);
  const { available: amazonAvailable } = useAmazonModule(userId);
  // Solo superadmin ve el badge de crones fallidos.
  const showCronAlert = userRole === "superadmin";
  const { data: cronFailed = 0 } = useCronAlertCount(showCronAlert);

  const handleLogout = async () => {
    try {
      const { auditLog } = await import("@/lib/audit");
      await auditLog({ action: "auth.logout", entity_type: "user", module: "auth", severity: "info" });
    } catch { /* ignore */ }
    await supabase.auth.signOut();
    // Limpia el contexto del usuario en Sentry para que próximos errores
    // (en pantalla de login) no se atribuyan al usuario anterior.
    Sentry.setUser(null);
    toast({
      title: "Sesión cerrada",
      description: "Has cerrado sesión correctamente.",
    });
    navigate("/auth");
  };

  const allItems = [
    { title: "Panel",               url: "/dashboard/overview",       icon: LayoutDashboard, group: "general" },
    { title: "Inventario",          url: "/dashboard",                icon: Package,         group: "logistica" },
    { title: "Movimientos",         url: "/dashboard/movements",      icon: TrendingUp,      group: "logistica" },
    { title: "Pedidos",             url: "/dashboard/orders",         icon: ShoppingCart,    group: "logistica" },
    { title: "Envíos",              url: "/dashboard/amazon",         icon: Truck,           group: "logistica", dynamic: "amazon" as const },
    { title: "Facturación",         url: "/dashboard/invoices",       icon: FileText,        group: "facturacion" },
    { title: "FacturaPro",          url: "/dashboard/facturapro",     icon: Receipt,         group: "facturacion" },
    { title: "Auditor",             url: "/dashboard/check-invoices", icon: FileSearch,      group: "facturacion" },
    { title: "Usuarios",            url: "/dashboard/users",          icon: UserIcon,        group: "gestion" },
  ];

  const GROUPS: Array<{ key: string; label: string }> = [
    { key: "general",     label: "Resumen" },
    { key: "logistica",   label: "Logística" },
    { key: "facturacion", label: "Facturación" },
    { key: "gestion",     label: "Gestión" },
  ];

  // Filter by module permissions; dynamic modules use data-based availability.
  const items = allItems.filter((item: any) => {
    // Envíos: solo admin/superadmin (además de que el módulo esté conectado).
    if (item.dynamic === "amazon") return amazonAvailable && (userRole === "admin" || userRole === "superadmin");
    const moduleKey = MODULE_KEY_MAP[item.url];
    return moduleKey ? hasAccess(moduleKey) : true;
  });

  const itemsByGroup = (groupKey: string) => items.filter((i: any) => i.group === groupKey);

  const showSettings = hasAccess("settings");
  const showAdmin = (userRole === "admin" || userRole === "superadmin") && hasAccess("administration");
  const showPermissions = userRole === "superadmin";

  return (
    <Sidebar className={state === "collapsed" ? "w-14" : "w-[18rem]"} collapsible="icon">
      <SidebarContent>
        <div className="px-3 py-4 border-b border-sidebar-border">
          {state !== "collapsed" && (
            <div className="flex items-center justify-center">
              <img src={nannukLogo} alt="Nannuk Logística" className="h-14 w-auto max-w-full object-contain" />
            </div>
          )}
          {state === "collapsed" && (
            <div className="flex justify-center items-center">
              <img src={nannukIcon} alt="Nannuk" className="w-10 h-10 rounded-lg object-contain" />
            </div>
          )}
        </div>

        {GROUPS.map((group) => {
          const groupItems = itemsByGroup(group.key);
          if (groupItems.length === 0) return null;
          return (
            <SidebarGroup key={group.key} className="py-0">
              {state !== "collapsed" && (
                <SidebarGroupLabel className="text-xs uppercase tracking-wider opacity-60 px-2 pt-2 pb-0.5 h-auto">
                  {group.label}
                </SidebarGroupLabel>
              )}
              <SidebarGroupContent>
                <SidebarMenu className="gap-1">
                  {groupItems.map((item) => (
                    <SidebarMenuItem key={item.url}>
                      <SidebarMenuButton asChild className="h-11 text-lg [&>svg]:!size-5">
                        <NavLink
                          to={item.url}
                          end={item.url === "/dashboard"}
                          className="hover:bg-sidebar-accent"
                          activeClassName="bg-sidebar-accent text-sidebar-primary font-medium"
                          onMouseEnter={() => prefetchRoute(item.url)}
                          onFocus={() => prefetchRoute(item.url)}
                        >
                          <item.icon className="h-5 w-5" />
                          {state !== "collapsed" && <span>{item.title}</span>}
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          );
        })}

        <div className="mt-auto border-t border-sidebar-border p-2">
          <TooltipProvider delayDuration={200}>
            <div className="flex items-center justify-center gap-1 flex-wrap">
              {showAdmin && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <NavLink to="/dashboard/administration" className="relative inline-flex items-center justify-center h-9 w-9 rounded-md hover:bg-sidebar-accent" activeClassName="bg-sidebar-accent text-sidebar-primary" onMouseEnter={() => prefetchRoute("/dashboard/administration")} onFocus={() => prefetchRoute("/dashboard/administration")}>
                      <Settings2 className="h-4 w-4" />
                      {cronFailed > 0 && (
                        <span
                          className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] px-1 rounded-full bg-destructive text-destructive-foreground text-[9px] font-semibold flex items-center justify-center"
                          aria-label={`${cronFailed} cron${cronFailed === 1 ? "" : "es"} fallido${cronFailed === 1 ? "" : "s"}`}
                        >
                          {cronFailed}
                        </span>
                      )}
                    </NavLink>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p>Administración{cronFailed > 0 ? ` · ${cronFailed} cron${cronFailed === 1 ? "" : "es"} fallido${cronFailed === 1 ? "" : "s"}` : ""}</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {showSettings && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <NavLink to="/dashboard/settings" className="inline-flex items-center justify-center h-9 w-9 rounded-md hover:bg-sidebar-accent" activeClassName="bg-sidebar-accent text-sidebar-primary" onMouseEnter={() => prefetchRoute("/dashboard/settings")} onFocus={() => prefetchRoute("/dashboard/settings")}>
                      <Settings className="h-4 w-4" />
                    </NavLink>
                  </TooltipTrigger>
                  <TooltipContent side="top"><p>Ajustes</p></TooltipContent>
                </Tooltip>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <span><NotificationBell /></span>
                </TooltipTrigger>
                <TooltipContent side="top"><p>Avisos</p></TooltipContent>
              </Tooltip>
<Tooltip>
                <TooltipTrigger asChild>
                  <button onClick={handleLogout} className="inline-flex items-center justify-center h-9 w-9 rounded-md hover:bg-sidebar-accent text-destructive">
                    <LogOut className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top"><p>Cerrar sesión</p></TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>
        </div>
      </SidebarContent>
    </Sidebar>
  );
};

const DashboardLayout = ({ children }: DashboardLayoutProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userRole, setUserRole] = useState<string>("user");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const qc = useQueryClient();

  // Prefetch en idle de las queries más usadas tras el login. Cuando el
  // usuario entra a Pedidos / Envíos / Movimientos los datos ya están en
  // cache y la navegación es instantánea.
  useEffect(() => {
    if (!session) return;
    const run = () => {
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();

      qc.prefetchQuery({
        queryKey: ["orders"],
        staleTime: 5 * 60 * 1000,
        queryFn: async () => {
          const [amazonOrders, movements] = await Promise.all([
            sq(
              (supabase as any)
                .from("amazon_orders")
                .select("id, order_number, tracking_number, buyer_name, created_at, quantity, status, transportista, products(name, sku), clients(alias)")
                .gte("created_at", thirtyDaysAgo)
                .order("created_at", { ascending: false })
                .limit(5000)
            ),
            sq(
              (supabase as any)
                .from("movements")
                .select("id, type, quantity, notes, referencia, tracking_number, destinatario, transportista, created_at, products(name, sku), clients(alias)")
                .eq("type", "salida")
                .gte("created_at", thirtyDaysAgo)
                .order("created_at", { ascending: false })
                .limit(5000)
            ),
          ]);
          return { amazonOrders: (amazonOrders as any) || [], movements: (movements as any) || [] };
        },
      }).catch(() => {});

      qc.prefetchQuery({
        queryKey: ["productList"],
        staleTime: 5 * 60 * 1000,
        queryFn: () => sq((supabase as any).from("products").select("id, name, sku").order("name")),
      }).catch(() => {});

      // Prefetch primera página de inventario para render inmediato.
      qc.prefetchQuery({
        queryKey: ["inventory", "data", { page: 1, pageSize: 30 }],
        staleTime: 5 * 60 * 1000,
        queryFn: async () => {
          const { data, error } = await (supabase as any)
            .from("products")
            .select(
              "id, name, sku, stock, image_url, asin, barcode, client_id, expiry_date, observations, estanteria, modulo, nivel, base_product_id, clients(name, alias)"
            )
            .is("base_product_id", null)
            .order("name")
            .range(0, 29);
          if (error) throw error;
          return data || [];
        },
      }).catch(() => {});

      qc.prefetchQuery({
        queryKey: ["clients"],
        staleTime: 5 * 60 * 1000,
        queryFn: () => sq((supabase as any).from("clients").select("id, name, alias").order("name")),
      }).catch(() => {});

    };

    if ("requestIdleCallback" in window) {
      (window as any).requestIdleCallback(run, { timeout: 3000 });
    } else {
      setTimeout(run, 500);
    }
  }, [session, qc]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        if (!session) {
          navigate("/auth");
        } else {
          setTimeout(() => {
            fetchUserRole(session.user.id);
          }, 0);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session) {
        fetchUserRole(session.user.id);
      } else {
        navigate("/auth");
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const fetchUserRole = async (userId: string) => {
    try {
      const { data, error } = await (supabase as any)
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .order("role", { ascending: true })
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        const role = (data as any).role as string;
        setUserRole(role);
        // Identifica al usuario en Sentry para que los errores reportados
        // sepan quién los vio (id, email, rol). Si Sentry no está activo
        // (sin DSN) esto es no-op.
        Sentry.setUser({ id: userId, email: user?.email, role } as any);
      }
    } catch (error) {
      console.error("Error fetching user role:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <img src={nannukIcon} alt="Nannuk" className="h-16 w-16 mx-auto mb-4 animate-pulse" />
          <p className="text-muted-foreground">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <DashboardSidebar userRole={userRole} userEmail={user.email} userId={user.id} />
        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-14 border-b border-border flex items-center px-2 sm:px-4 bg-card">
            <SidebarTrigger />
            <div className="ml-auto flex items-center gap-4 min-w-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/dashboard/profile")}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground min-w-0"
              >
                <UserIcon className="h-4 w-4 shrink-0" />
                <span className="truncate max-w-[45vw] sm:max-w-none">{user.email}</span>
              </Button>
            </div>
          </header>
          <main className="flex-1 p-3 sm:p-6 overflow-auto min-w-0">
            {children}
          </main>
          <footer className="min-h-12 py-2 border-t border-border flex items-center justify-center bg-card text-xs sm:text-sm text-muted-foreground text-center px-2 flex-wrap">
            <span>Nannuk Logística Copyright 2026© Design by</span>
            <a href="https://jdsandev.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline ml-1">
              JDSANDEV
            </a>
          </footer>
        </div>
      </div>
    </SidebarProvider>
  );
};

/** Wrapper for React Router nested routes — keeps layout mounted across navigations */
export const DashboardLayoutRoute = () => (
  <DashboardLayout>
    <Outlet />
  </DashboardLayout>
);

export default DashboardLayout;
